import React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { getVendor, updateVendorProfile } from '@/services/api/vendors';
import { translations } from '@/locale';
import { imageSchema } from '@/validation';
import { VendorProfileUpdate } from '@/domains';

import LoadData from '@/components/LoadData';
import If from '@/components/If';
import Loading from '@/components/Loading';
import Placeholder from '@/components/Placeholder';
import { ImageInputWithValidation } from '@/components/Input/ImageInput';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';

const formSchema = z.object({
  active: z.boolean(),
  image: imageSchema,
  name: z.string().min(1, 'Name is required'),
  description: z.string().min(1, 'Description is required'),
  address: z.string().min(1, 'Address is required'),
  dailyOrderLimit: z.number().optional(),
  orderReportEmails: z.string().optional(),
  ebrizaCategory: z.string().nullable()
});

type FormValues = z.infer<typeof formSchema>;

export interface Props {
  id: string;
}

interface VendorFormProps {
  vendor: any;
  onSubmit: (values: FormValues) => Promise<void>;
}

const VendorForm: React.FC<VendorFormProps> = ({ vendor, onSubmit }) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      active: vendor.active,
      name: vendor.name,
      description: vendor.description,
      address: vendor.address,
      image: { url: vendor.imageUrl, thumbnailUrl: vendor.thumbnailUrl },
      dailyOrderLimit: vendor.dailyOrderLimit,
      orderReportEmails: vendor.orderReportEmails,
      ebrizaCategory: vendor.ebrizaCategory
    }
  });

  return (
    <Card>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="image"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cover Image</FormLabel>
                  <FormControl>
                    <ImageInputWithValidation
                      onImageUploaded={(image) => {
                        form.setValue('image', image);
                      }}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter vendor name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter a short description about the restaurant"
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter restaurant address" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="pt-4">
              <h3 className="text-lg font-medium">Status</h3>
              <p className="text-sm text-muted-foreground">
                Disabling this vendor will prevent them from being displayed in the application.
              </p>
            </div>

            <FormField
              control={form.control}
              name="active"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2">
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <FormLabel className="!mt-0">Active</FormLabel>
                </FormItem>
              )}
            />

            <div className="pt-4">
              <h3 className="text-lg font-medium">Daily Order Limit</h3>
              <p className="text-sm text-muted-foreground">
                Limit the number of orders this vendor can fulfill in a day.
              </p>
            </div>

            <FormField
              control={form.control}
              name="dailyOrderLimit"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Enter the daily order limit"
                      {...field}
                      onChange={(e) => field.onChange(e.target.valueAsNumber)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="pt-4">
              <h3 className="text-lg font-medium">Scheduled Reports</h3>
              <p className="text-sm text-muted-foreground">
                Add a list of semicolon separated emails where you want to send order reports. <br />
                The platform will send reports at 18:00 Monday through Thursday and Sunday for the next day.
              </p>
            </div>

            <FormField
              control={form.control}
              name="orderReportEmails"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input placeholder="ex.: foo@bar.com; bar@foo.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="pt-4">
              <h3 className="text-lg font-medium">Ebriza</h3>
              <p className="text-sm text-muted-foreground">
                Enter Category Name to enable automatic product sync with Ebriza.
              </p>
            </div>

            <FormField
              control={form.control}
              name="ebrizaCategory"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ebriza Category</FormLabel>
                  <FormControl>
                    <Input placeholder="Ebriza category name: Nomenclator > AFIȘARE PRODUSE > APLICATIE" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" disabled={form.formState.isSubmitting || !form.formState.isDirty}>
              {form.formState.isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

const VendorGeneralTab: React.FC<Props> = ({ id }) => {
  return (
    <React.Fragment>
      <LoadData id={id} load={getVendor}>
        {({ value: vendor, loading }) => (
          <If
            condition={loading}
            then={() => <Loading visible={loading} />}
            else={() => (
              <If
                condition={!vendor}
                then={() => (
                  <Placeholder
                    className="h-full justify-center"
                    image="bell"
                    size="xs"
                    title="Oups"
                    description="Vendor not found. Please try again later."
                  />
                )}
                else={() => {
                  async function handleSubmit(values: FormValues) {
                    try {
                      await updateVendorProfile(id, values);
                    } catch (error) {
                      console.error('Failed to update vendor:', error);
                    }
                  }

                  return <VendorForm vendor={vendor} onSubmit={handleSubmit} />;
                }}
              />
            )}
          />
        )}
      </LoadData>
    </React.Fragment>
  );
};

export default VendorGeneralTab;
